//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { InstagramReelsPostCustomizations } from '@bvengineering/universal-scheduling-gateway-service-types';

import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { InstagramStoryGroupClipCustomizationType } from '@models/IScheduledInstagramStoryGroup';
import InstagramHashtag from '@models/InstagramHashtag';
import {
  IInstagramFeedAutomaticFormState,
  IInstagramFeedManualFormState,
  IInstagramReelsAutomaticFormState,
  IInstagramReelsManualFormState,
  IInstagramStoryAutomaticFormState,
  IInstagramStoryManualFormState,
} from '@universal-scheduling/models/SchedulerPostDetailsModels';
import { parseHashtags } from '@utils/stringUtils';

// TODO PD-243507: Set automatic or manual default form state based on user settings
export const DEFAULT_INSTAGRAM_POST_DETAILS_AUTOMATIC_FORM_STATE: IInstagramFeedAutomaticFormState =
  {
    postType: UniversalPostType.InstagramFeedAutomaticPost,
    postNow: false,
  };

export const DEFAULT_INSTAGRAM_POST_DETAILS_MANUAL_FORM_STATE: IInstagramFeedManualFormState =
  {
    postType: UniversalPostType.InstagramFeedManualPost,
    postNow: false,
  };

export const DEFAULT_INSTAGRAM_REELS_DETAILS_AUTOMATIC_FORM_STATE: IInstagramReelsAutomaticFormState =
  {
    postType: UniversalPostType.InstagramReelsAutomaticPost,
    postNow: false,
    shareToFeed: true,
  };

export const DEFAULT_INSTAGRAM_REELS_DETAILS_MANUAL_FORM_STATE: IInstagramReelsManualFormState =
  {
    postType: UniversalPostType.InstagramReelsManualPost,
    postNow: false,
    shareToFeed: true,
  };

export const DEFAULT_INSTAGRAM_STORY_DETAILS_AUTOMATIC_FORM_STATE: IInstagramStoryAutomaticFormState =
  {
    postType: UniversalPostType.InstagramStoryGroupAutomaticPost,
    postNow: false,
  };

export const DEFAULT_INSTAGRAM_STORY_DETAILS_MANUAL_FORM_STATE: IInstagramStoryManualFormState =
  {
    postType: UniversalPostType.InstagramStoryGroupPost,
    postNow: false,
  };

export const LOCATION_SECTION_PLACEHOLDER = {
  Auto: 'Search for a location',
  Manual: 'Unavailable when posting manually',
} as const;

export const LOCATION_SECTION_TOOLTIP =
  'In order to tag locations, you must set the post method to automatically post to Instagram.';

export const MAX_IG_HASHTAG_COUNT = 30;
export const MAX_IG_CHARACTER_COUNT = 2200;

export const InstagramPostDetailsFormFieldErrorText = {
  TextTooLong: `Please enter ${MAX_IG_CHARACTER_COUNT} characters or less.`,
  TooManyHashtags: `Please include ${MAX_IG_HASHTAG_COUNT} hashtags or less.`,
} as const;

// Setting a default limit of 30 hashtags
export const TOP_INSTAGRAM_HASHTAG_RECOMMENDATIONS_LIMIT = 30;

export const filterRecommendedHashtags = (
  recommendedHashtags: InstagramHashtag[],
  caption: string | undefined,
  firstComment: string | undefined,
) => {
  const captionHashtags = parseHashtags(caption ?? '').map((tag) =>
    tag.toLowerCase(),
  );
  const firstCommentHashtags = parseHashtags(firstComment ?? '').map((tag) =>
    tag.toLowerCase(),
  );

  const existingHashtags = [...captionHashtags, ...firstCommentHashtags];

  return recommendedHashtags.map((tag) => ({
    value: tag,
    shouldShow: !existingHashtags.includes(tag),
  }));
};

export const PostDetailsTaggedUserSectionPlaceholder = {
  Auto: 'Click on the photo to tag public users.',
  Manual: 'Unavailable when posting manually.',
  Video: 'Unavailable for video posts.',
  NoMedia: 'Select an image to tag public users.',
  NoAccount: 'No public users tagged.',
  IsPosted: "Tagged users can't be changed after a post has been published.",
} as const;

export const INSTAGRAM_TAGGED_USERS_TOOLTIP =
  "Users that will be tagged on your post's media.";

export const INSTAGRAM_META_TAGS_TOOLTIP =
  "Products from your Meta product catalog that will appear on your post's media. When selected, the user will go to the product's page on your Instagram shop.";

export const PostDetailsTaggedProductSectionPlaceholder = {
  Auto: 'Click on the photo to add Instagram Shop Tags.',
  Manual: 'Unavailable when posting manually.',
  NoMedia: 'Select media to add Instagram Shop Tags.',
  NoAccount: 'No Instagram Shop Tags.',
  IsPosted:
    "Instagram Shop Tags can't be changed after a post has been published.",
} as const;

// TODO PD-250138: Migrate other uses to use this
export const InstagramStoryGroupClipCustomizationTypeLabel: Record<
  InstagramStoryGroupClipCustomizationType,
  string
> = {
  [InstagramStoryGroupClipCustomizationType.SwipeUpUrl]: 'Link',
  [InstagramStoryGroupClipCustomizationType.TextOverlay]: 'Text Overlay',
  [InstagramStoryGroupClipCustomizationType.Location]: 'Location',
  [InstagramStoryGroupClipCustomizationType.AtMention]: '@ Mention',
  [InstagramStoryGroupClipCustomizationType.Hashtag]: 'Hashtag',
  [InstagramStoryGroupClipCustomizationType.Poll]: 'Poll',
  [InstagramStoryGroupClipCustomizationType.Question]: 'Question',
  [InstagramStoryGroupClipCustomizationType.Gif]: 'GIF',
  [InstagramStoryGroupClipCustomizationType.Other]: 'Other',
} as const;

// TODO PD-250138: Migrate other uses to use this
export const storyCustomizationPlaceholderText: Record<
  InstagramStoryGroupClipCustomizationType,
  string
> = {
  [InstagramStoryGroupClipCustomizationType.SwipeUpUrl]:
    'Add a link to your story',
  [InstagramStoryGroupClipCustomizationType.Hashtag]:
    'Add a hashtag to your story',
  [InstagramStoryGroupClipCustomizationType.TextOverlay]:
    'Add text to your story',
  [InstagramStoryGroupClipCustomizationType.Location]:
    'Add a location to your story',
  [InstagramStoryGroupClipCustomizationType.AtMention]:
    'Mention a username in your story',
  [InstagramStoryGroupClipCustomizationType.Poll]: 'Add a Poll to your story',
  [InstagramStoryGroupClipCustomizationType.Question]:
    'Add a question to your story',
  [InstagramStoryGroupClipCustomizationType.Gif]:
    'Describe a GIF to add to your story',
  [InstagramStoryGroupClipCustomizationType.Other]:
    'Describe a sticker to add to your story',
} as const;

// TODO PD-250138: Migrate other uses to use this
export const UNAVAILABLE_FOR_AUTOMATIC_TEXT =
  'Unavailable when posting automatically.';
export const STORY_CLIP_NO_MEDIA_TOOLTIP = `Select media to add links and notes to your Story.`;
export const STORY_CLIP_TOOLTIP =
  'Select a clip to add a link or notes to your story.';
export const STORY_NOTES_TOOLTIP = `Notes help you keep track of any text overlays, hashtags, mentions, locations tags, or more that you'll want to add to your story. We'll make it easy to copy and paste your notes when it's time to post.`;

export const STORY_URL_SECTION_TOOLTIP = `Add a link to your Story. Link to a Showroom to make your Story shoppable!`;

// TODO PD-250138: Migrate other uses to use this
export interface IReelsCreativeNote {
  type: InstagramReelsCreativeNotesType;
  index?: number;
  value: string;
}

// TODO PD-250138: Migrate other uses to use this
export type InstagramReelsCreativeNotesType = keyof Omit<
  InstagramReelsPostCustomizations,
  'caption' | 'location' | 'userTags'
>;

// TODO PD-250138: Migrate other uses to use this
export const OrderedInstagramReelsCreativeNotesTypes: Readonly<
  InstagramReelsCreativeNotesType[]
> = ['textOverlay', 'audio', 'effects', 'stickers', 'other'] as const;

export const INSTAGRAM_USER_IFRAME_SHOW_DELAY = 500;

export const INSTAGRAM_COVER_UPLOAD_LIMIT = 8 * 1024 * 1024; // 8MB

export const INSTAGRAM_COVER_UPLOAD_SIZE_ERROR =
  'Custom cover image is required to be less than 8MB.';
